import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CrownOutlined } from '@ant-design/icons';
import { useUpdateTenantRoleMutation } from '@/api/auth/mutations';
import { STAFF_TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { useTenantRoleOptions, type TenantRoleOption } from '@/hooks/useTenantRoleOptions';
import { RoleEditorModal, type RoleEditorOption } from './RoleEditorModal';

interface TenantRoleEditorProps {
  clientId: string;
  userId: string;
  currentRole: TenantRoleType;
  userName?: string;
  /** Roles offered in the modal; defaults to staff roles (Owner, Employee). Include CUSTOMER to move users between staff and customers. */
  allowedRoles?: readonly TenantRoleType[];
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/** Adds the owner crown to a translated tenant role option. */
const toEditorOption = (option: TenantRoleOption): RoleEditorOption<TenantRoleType> => ({
  ...option,
  icon: option.value === 'OWNER' ? <CrownOutlined /> : undefined
});

/**
 * Badge + modal for changing a member's role within one tenant.
 */
export function TenantRoleEditor({
  clientId,
  userId,
  currentRole,
  userName,
  allowedRoles = STAFF_TENANT_ROLES,
  onSuccess,
  onError
}: TenantRoleEditorProps) {
  const { t } = useTranslation('manage');
  const updateMutation = useUpdateTenantRoleMutation(clientId);
  const roleOptions = useTenantRoleOptions(allowedRoles);
  const allRoleOptions = useTenantRoleOptions();

  const options = useMemo(() => roleOptions.map(toEditorOption), [roleOptions]);
  const allOptions = useMemo(() => allRoleOptions.map(toEditorOption), [allRoleOptions]);

  return (
    <RoleEditorModal
      currentRole={currentRole}
      options={options}
      findOption={(role) => allOptions.find((option) => option.value === role)}
      save={(tenantRole) => updateMutation.mutateAsync({ userId, tenantRole })}
      saving={updateMutation.isPending}
      userName={userName}
      onSuccess={onSuccess}
      onError={onError}
      texts={{
        tooltip: t('employees.tenantRoleEditor.editTooltip', 'Change tenant role'),
        changeTitle: t('employees.tenantRoleEditor.changeTitle', 'Change tenant role'),
        editingFor: t('employees.tenantRoleEditor.editingFor', 'Editing role for'),
        selectNewRole: t('employees.tenantRoleEditor.selectNewRole', 'Select new role'),
        current: t('employees.tenantRoleEditor.current', 'Current'),
        confirmTitle: t('employees.tenantRoleEditor.confirmTitle', 'Confirm role change'),
        confirmDescription: (role) =>
          t('employees.tenantRoleEditor.confirmDescription', { role, defaultValue: `Change role to ${role}?` }),
        saveChanges: t('employees.tenantRoleEditor.saveChanges', 'Save changes'),
        selectRole: t('employees.tenantRoleEditor.selectRole', 'Please select a role'),
        noChanges: t('employees.tenantRoleEditor.noChanges', 'No changes to save'),
        updateSuccess: t('employees.tenantRoleEditor.updateSuccess', 'Role updated successfully'),
        updateError: t('employees.tenantRoleEditor.updateError', 'Failed to update role')
      }}
    />
  );
}
